"use client";

import React from "react";
import { cn } from "../../lib/utils";
import { Progress } from "../Progress";

export interface PasswordStrengthMeterProps {
  password?: string;
  onStrengthChange?: (strength: number) => void;
  t?: (key: string) => string;
}

export function PasswordStrengthMeter({
  password = "",
  onStrengthChange,
  t,
}: PasswordStrengthMeterProps) {
  const calculateStrength = (pwd: string) => {
    let strength = 0;

    const hasMinLength = pwd.length >= 8;
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecial = /[^A-Za-z0-9]/.test(pwd);

    if (hasMinLength) strength += 34;
    if (hasNumber) strength += 33;
    if (hasSpecial) strength += 33;

    return Math.min(strength, 100);
  };

  const strength = calculateStrength(password);

  React.useEffect(() => {
    if (onStrengthChange) {
      onStrengthChange(strength);
    }
  }, [strength, onStrengthChange]);

  const getStrengthText = (s: number) => {
    if (s === 0) return "";
    if (s < 40) return t ? t("passwordStrength.weak") : "Weak";
    if (s < 70) return t ? t("passwordStrength.medium") : "Medium";
    return t ? t("passwordStrength.strong") : "Strong";
  };

  const getStrengthColor = (s: number) => {
    if (s < 40) return "bg-red-500";
    if (s < 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  if (password.length === 0) {
    return null;
  }

  const hasMinLength = password.length >= 8;
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);

  return (
    <div className="w-full space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          {t ? t("passwordStrength.passwordStrength") : "Password Strength"}
        </span>
        <span
          className={cn(
            "text-sm font-bold",
            getStrengthColor(strength).replace("bg-", "text-")
          )}
        >
          {getStrengthText(strength)}
        </span>
      </div>

      <Progress
        value={strength}
        className={cn("h-1.5", getStrengthColor(strength))}
      />

      <ul className="text-xs text-gray-500 space-y-1">
        <li
          className={cn("flex items-center", {
            "text-green-600": hasMinLength,
          })}
        >
          <span className="mr-2 text-sm">
            {hasMinLength ? "✓" : "○"}
          </span>
          {t ? t("passwordStrength.atLeast8Chars") : "At least 8 characters"}
        </li>

        <li
          className={cn("flex items-center", {
            "text-green-600": hasNumber,
          })}
        >
          <span className="mr-2 text-sm">
            {hasNumber ? "✓" : "○"}
          </span>
          {t ? t("passwordStrength.number") : "Number"}
        </li>

        <li
          className={cn("flex items-center", {
            "text-green-600": hasSpecial,
          })}
        >
          <span className="mr-2 text-sm">
            {hasSpecial ? "✓" : "○"}
          </span>
          {t
            ? t("passwordStrength.specialCharacter")
            : "Special character"}
        </li>
      </ul>
    </div>
  );
}

export default PasswordStrengthMeter;
